/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import JXG from 'jsxgraph';

/* ---- Types ---- */
export interface GraphElement {
  type: 'point' | 'line' | 'segment' | 'function-curve';
  coordinates?: [number, number];
  points?: [number, number][];
  expression?: string;
  color?: string;
  label?: string;
}

export interface GraphConfig {
  axes: {
    xMin: number; xMax: number;
    yMin: number; yMax: number;
    gridVisible: boolean;
    axisLabels?: { x: string; y: string };
  };
  displayElements: GraphElement[];
  expectedAnswer: GraphElement[];
  tolerance: number;
  interactionMode: 'plot-points' | 'draw-line' | 'draw-function' | 'free';
  functions?: { expression: string; color: string; label?: string }[];
}

interface GraphAuthoringCanvasProps {
  config: GraphConfig;
  onChange: (config: GraphConfig) => void;
}

type ToolMode = 'select' | 'add-point' | 'add-line-p1' | 'add-line-p2';
type TargetLayer = 'display' | 'expected';

let boardCounter = 0;

const GraphAuthoringCanvas: React.FC<GraphAuthoringCanvasProps> = ({ config, onChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<any>(null);
  const boardIdRef = useRef(`graph-board-${++boardCounter}`);

  const [tool, setTool] = useState<ToolMode>('select');
  const [targetLayer, setTargetLayer] = useState<TargetLayer>('expected');
  const [funcInput, setFuncInput] = useState('');
  const [lineP1, setLineP1] = useState<[number, number] | null>(null);

  /** Rebuild the board from scratch based on current config */
  const rebuildBoard = useCallback(() => {
    if (!containerRef.current) return;
    // Free old board
    if (boardRef.current) {
      try { JXG.JSXGraph.freeBoard(boardRef.current); } catch { /* ignore */ }
      boardRef.current = null;
    }

    const board = JXG.JSXGraph.initBoard(boardIdRef.current, {
      boundingbox: [config.axes.xMin, config.axes.yMax, config.axes.xMax, config.axes.yMin],
      axis: true,
      grid: config.axes.gridVisible,
      showCopyright: false,
      showNavigation: false,
      keepAspectRatio: false,
    });
    boardRef.current = board;

    // Render display elements (blue)
    config.displayElements.forEach((el) => {
      renderElement(board, el, el.color || '#3b82f6', false);
    });

    // Render expected answer elements (green)
    config.expectedAnswer.forEach((el) => {
      renderElement(board, el, el.color || '#10b981', false);
    });

    // Render functions
    (config.functions || []).forEach((fn) => {
      try {
        board.create('functiongraph', [buildFn(fn.expression)], {
          strokeColor: fn.color || '#ef4444', strokeWidth: 2,
          name: fn.label || '', withLabel: !!fn.label,
        });
      } catch { /* invalid expression */ }
    });
  }, [config]);

  useEffect(() => {
    rebuildBoard();
    return () => {
      if (boardRef.current) {
        try { JXG.JSXGraph.freeBoard(boardRef.current); } catch { /* ignore */ }
        boardRef.current = null;
      }
    };
  }, [rebuildBoard]);

  /** Handle click on the board */
  const handleBoardClick = useCallback((e: React.MouseEvent) => {
    const board = boardRef.current;
    if (!board || tool === 'select') return;

    const rect = containerRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const coords = new JXG.Coords(JXG.COORDS_BY_SCREEN, [x, y], board);
    const px = Math.round(coords.usrCoords[1] * 10) / 10;
    const py = Math.round(coords.usrCoords[2] * 10) / 10;

    if (tool === 'add-point') {
      const el: GraphElement = { type: 'point', coordinates: [px, py], label: `(${px},${py})` };
      addElement(el);
    } else if (tool === 'add-line-p1') {
      setLineP1([px, py]);
      setTool('add-line-p2');
    } else if (tool === 'add-line-p2' && lineP1) {
      const el: GraphElement = { type: 'line', points: [lineP1, [px, py]] };
      addElement(el);
      setLineP1(null);
      setTool('select');
    }
  }, [tool, lineP1, targetLayer, config]);

  const addElement = (el: GraphElement) => {
    const newConfig = { ...config };
    if (targetLayer === 'display') {
      newConfig.displayElements = [...config.displayElements, el];
    } else {
      newConfig.expectedAnswer = [...config.expectedAnswer, el];
    }
    onChange(newConfig);
  };

  const addFunction = () => {
    if (!funcInput.trim()) return;
    const newConfig = { ...config };
    newConfig.functions = [...(config.functions || []), { expression: funcInput, color: 'var(--status-danger)' }];
    onChange(newConfig);
    setFuncInput('');
  };

  const removeElement = (layer: 'display' | 'expected' | 'functions', idx: number) => {
    const newConfig = { ...config };
    if (layer === 'display') {
      newConfig.displayElements = config.displayElements.filter((_, i) => i !== idx);
    } else if (layer === 'expected') {
      newConfig.expectedAnswer = config.expectedAnswer.filter((_, i) => i !== idx);
    } else {
      newConfig.functions = (config.functions || []).filter((_, i) => i !== idx);
    }
    onChange(newConfig);
  };

  const updateAxes = (field: string, value: any) => {
    onChange({ ...config, axes: { ...config.axes, [field]: value } });
  };

  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 10, padding: 16, background: 'var(--surface-page)' }}>
      {/* Axes config */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'end' }}>
        <Form.Group style={{ width: 80 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>X Min</Form.Label>
          <Form.Control size="sm" type="number" value={config.axes.xMin} onChange={e => updateAxes('xMin', Number(e.target.value))} />
        </Form.Group>
        <Form.Group style={{ width: 80 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>X Max</Form.Label>
          <Form.Control size="sm" type="number" value={config.axes.xMax} onChange={e => updateAxes('xMax', Number(e.target.value))} />
        </Form.Group>
        <Form.Group style={{ width: 80 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>Y Min</Form.Label>
          <Form.Control size="sm" type="number" value={config.axes.yMin} onChange={e => updateAxes('yMin', Number(e.target.value))} />
        </Form.Group>
        <Form.Group style={{ width: 80 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>Y Max</Form.Label>
          <Form.Control size="sm" type="number" value={config.axes.yMax} onChange={e => updateAxes('yMax', Number(e.target.value))} />
        </Form.Group>
        <Form.Check
          type="checkbox" label="Grid" style={{ fontSize: 12, marginBottom: 6 }}
          checked={config.axes.gridVisible}
          onChange={e => updateAxes('gridVisible', e.target.checked)}
        />
      </div>

      <div style={{ display: 'flex', gap: 16 }}>
        {/* Board */}
        <div>
          {/* Toolbar */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 6, flexWrap: 'wrap' }}>
            <Button size="sm" variant={tool === 'select' ? 'primary' : 'secondary'} onClick={() => { setTool('select'); setLineP1(null); }}>Select</Button>
            <Button size="sm" variant={tool === 'add-point' ? 'primary' : 'secondary'} onClick={() => { setTool('add-point'); setLineP1(null); }}>+ Point</Button>
            <Button size="sm" variant={tool === 'add-line-p1' || tool === 'add-line-p2' ? 'primary' : 'secondary'} onClick={() => { setTool('add-line-p1'); setLineP1(null); }}>
              + Line {tool === 'add-line-p2' ? '(click 2nd pt)' : ''}
            </Button>
            <Form.Select
              size="sm" style={{ width: 'auto', fontSize: 12 }}
              value={targetLayer}
              onChange={e => setTargetLayer(e.target.value as TargetLayer)}
            >
              <option value="expected">Target: Expected Answer</option>
              <option value="display">Target: Display (read-only for student)</option>
            </Form.Select>
          </div>

          <div
            ref={containerRef}
            id={boardIdRef.current}
            style={{ width: 500, height: 400, border: '1px solid var(--border-default)', borderRadius: 6, cursor: tool !== 'select' ? 'crosshair' : 'default' }}
            onClick={handleBoardClick}
          />

          {/* Function input */}
          <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
            <Form.Control
              size="sm" style={{ flex: 1 }}
              value={funcInput}
              onChange={e => setFuncInput(e.target.value)}
              placeholder="e.g.  Math.sin(x)  or  x*x - 3"
            />
            <Button size="sm" variant="secondary" onClick={addFunction} disabled={!funcInput.trim()}>+ Function</Button>
          </div>
        </div>

        {/* Right panel: element lists */}
        <div style={{ flex: 1, minWidth: 200, fontSize: 12 }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 700, fontSize: 12, color: 'var(--status-success)' }}>Expected Answer Elements</label>
            {config.expectedAnswer.length === 0 && <div style={{ color: '#aaa', fontSize: 11 }}>None — click on board to add</div>}
            {config.expectedAnswer.map((el, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                <span>{el.type}: {formatElement(el)}</span>
                <button type="button" onClick={() => removeElement('expected', i)} style={{ border: 'none', background: 'none', color: 'var(--status-danger)', cursor: 'pointer' }}>&times;</button>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 700, fontSize: 12, color: 'var(--brand-primary)' }}>Display Elements (shown to student)</label>
            {config.displayElements.length === 0 && <div style={{ color: '#aaa', fontSize: 11 }}>None</div>}
            {config.displayElements.map((el, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                <span>{el.type}: {formatElement(el)}</span>
                <button type="button" onClick={() => removeElement('display', i)} style={{ border: 'none', background: 'none', color: 'var(--status-danger)', cursor: 'pointer' }}>&times;</button>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 700, fontSize: 12, color: 'var(--status-danger)' }}>Functions</label>
            {(config.functions || []).length === 0 && <div style={{ color: '#aaa', fontSize: 11 }}>None</div>}
            {(config.functions || []).map((fn, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                <span>f(x) = {fn.expression}</span>
                <button type="button" onClick={() => removeElement('functions', i)} style={{ border: 'none', background: 'none', color: 'var(--status-danger)', cursor: 'pointer' }}>&times;</button>
              </div>
            ))}
          </div>

          {/* Interaction mode + tolerance */}
          <div style={{ marginTop: 12 }}>
            <Form.Group style={{ marginBottom: 8 }}>
              <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>Student Interaction Mode</Form.Label>
              <Form.Select size="sm" value={config.interactionMode} onChange={e => onChange({ ...config, interactionMode: e.target.value as any })}>
                <option value="plot-points">Plot Points</option>
                <option value="draw-line">Draw Line</option>
                <option value="draw-function">Draw Function</option>
                <option value="free">Free (points + lines)</option>
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>Grading Tolerance</Form.Label>
              <Form.Control size="sm" type="number" step="0.1" min="0.1" value={config.tolerance} onChange={e => onChange({ ...config, tolerance: Number(e.target.value) || 0.5 })} />
            </Form.Group>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---- Helpers ---- */

function renderElement(board: any, el: GraphElement, color: string, _isDraggable: boolean) {
  try {
    if (el.type === 'point' && el.coordinates) {
      board.create('point', el.coordinates, {
        name: el.label || '', size: 4, fillColor: color, strokeColor: color,
        fixed: true, withLabel: !!el.label, label: { fontSize: 10 },
      });
    } else if (el.type === 'line' && el.points && el.points.length >= 2) {
      const p1 = board.create('point', el.points[0], { visible: true, size: 2, fillColor: color, strokeColor: color, fixed: true, withLabel: false });
      const p2 = board.create('point', el.points[1], { visible: true, size: 2, fillColor: color, strokeColor: color, fixed: true, withLabel: false });
      board.create('line', [p1, p2], { strokeColor: color, strokeWidth: 2, fixed: true });
    } else if (el.type === 'segment' && el.points && el.points.length >= 2) {
      const p1 = board.create('point', el.points[0], { visible: true, size: 2, fillColor: color, strokeColor: color, fixed: true, withLabel: false });
      const p2 = board.create('point', el.points[1], { visible: true, size: 2, fillColor: color, strokeColor: color, fixed: true, withLabel: false });
      board.create('segment', [p1, p2], { strokeColor: color, strokeWidth: 2, fixed: true });
    } else if (el.type === 'function-curve' && el.expression) {
      board.create('functiongraph', [buildFn(el.expression)], {
        strokeColor: color, strokeWidth: 2, fixed: true,
      });
    }
  } catch { /* skip invalid */ }
}

function buildFn(expr: string): (x: number) => number {
  // Support common patterns: x^2, sin(x), etc.
  // eslint-disable-next-line no-new-func
  return new Function('x', `with(Math){return (${expr})}`) as (x: number) => number;
}

function formatElement(el: GraphElement): string {
  if (el.type === 'point' && el.coordinates) return `(${el.coordinates[0]}, ${el.coordinates[1]})`;
  if ((el.type === 'line' || el.type === 'segment') && el.points) return el.points.map(p => `(${p[0]},${p[1]})`).join(' → ');
  if (el.type === 'function-curve' && el.expression) return `f(x)=${el.expression}`;
  return '?';
}

export default GraphAuthoringCanvas;
